import React, { useState, useEffect, useMemo } from 'react';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Box,
  Button,
  Table,
  TextFilter,
  Pagination,
  CollectionPreferences,
  Link,
  Modal,
  StatusIndicator
} from "@cloudscape-design/components";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as api from "../api/api";
import { useNotifications } from "../hooks/NotificationsContext";
import { useLocalStorage } from "../common/localStorage";
import { DEFAULT_PREFERENCES, PAGE_SIZE_OPTIONS, sanitizePreferences } from "../constants/insightsConstants";
import { canRetryRun } from "../utils/insights/runStatus";

// feature 025: status del registro -> type de StatusIndicator de Cloudscape.
// Un registro sin status (anterior a la 025) se lee como COMPLETED (FR-027).
const STATUS_INDICATOR_TYPE = {
  IN_PROGRESS: "in-progress",
  COMPLETED: "success",
  FAILED: "error",
  EMPTY_PERIOD: "stopped",
};

const STATUS_LABEL_KEY = {
  IN_PROGRESS: "insights.statusInProgress",
  COMPLETED: "insights.statusCompleted",
  FAILED: "insights.statusFailed",
  EMPTY_PERIOD: "insights.statusEmptyPeriod",
};

// feature 025 (US3): origen de la corrida. Un registro sin origin se lee como MANUAL (FR-024).
const ORIGIN_LABEL_KEY = {
  MANUAL: "insights.originManual",
  BIWEEKLY_AUTO: "insights.originBiweeklyAuto",
};

const CX_SUMMARY_DEFAULT_PREFERENCES = {
  pageSize: DEFAULT_PREFERENCES.pageSize,
  wrapLines: true,
  visibleContent: ["datetime", "summaryName", "origin", "supervisors", "agents", "dateRange", "coverage", "actions"]
};

function CXSummary() {
  const { t } = useTranslation();
  const history = useHistory();
  const { addSummaryReadyListener } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  // Sorting state
  const [sortingColumn, setSortingColumn] = useState({ sortingColumn: "datetime" });
  const [sortingDescending, setSortingDescending] = useState(true);

  // Column preferences (feature 025, US2, T049: tamaño de página de la constante compartida, recordado por navegador)
  const [storedPreferences, setPreferences] = useLocalStorage('cx-summary-preferences', CX_SUMMARY_DEFAULT_PREFERENCES);
  const preferences = useMemo(
    () => sanitizePreferences(storedPreferences, CX_SUMMARY_DEFAULT_PREFERENCES),
    [storedPreferences]
  );

  // Selected agents
  const [selectedAgents, setSelectedAgents] = useState([]);

  // Load data when component mounts
  useEffect(() => {
    fetchHistoryData();
  }, []);

  // feature 025 (US4, T041): refresca el listado si llega un aviso mientras está montada
  useEffect(() => {
    const unsubscribe = addSummaryReadyListener(() => {
      fetchHistoryData();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSummaryReadyListener]);

  const fetchHistoryData = async () => {
    try {
      setLoading(true);
      console.log("Obteniendo historial de análisis...");

      const response = await api.getSummaryHistory();
      console.log("Respuesta del historial:", response);

      if (response && response.history && Array.isArray(response.history)) {
        console.log(`Se encontraron ${response.history.length} elementos en el historial`);
        setHistoryItems(response.history);
      } else {
        console.log("No se encontraron elementos en el historial");
        setHistoryItems([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Función helper para formatear rango de fechas
  const formatDateRange = (filters) => {
    if (!filters || !filters.startDate || !filters.endDate) return "-";

    try {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const startStr = startDate.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const endStr = endDate.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return `${startStr}-${endStr}`;
    } catch (error) {
      return "Rango inválido";
    }
  };

  // Función helper para formatear agentes
  const formatAgents = (agent) => {
    if (!agent) return "Todos";
    if (Array.isArray(agent)) {
      return agent.length > 0 ? agent.join(", ") : "Todos";
    }
    return agent;
  };

  // Función helper para formatear supervisores
  const formatSupervisors = (supervisor) => {
    if (!supervisor) return "Todos";
    if (Array.isArray(supervisor)) {
      return supervisor.length > 0 ? supervisor.join(", ") : "Todos";
    }
    return supervisor;
  };

  // Función helper para contar agentes
  const countAgents = (agent) => {
    if (!agent) return 0;
    if (Array.isArray(agent) && agent.length > 0) {
      return agent.length;
    }
    return 0;
  };

  // Función helper para formatear agentes al formato de link
  const formatLinkAgents = (agent) => {
    let count = countAgents(agent);
    if (count !== 0) {
      return t("insights.agentCount", { count: count });
    }
    return "Todos";
  }

  // Handler para navegar a nuevo resumen
  const handleNewSummary = () => {
    history.push('/insights-calls');
  };

  // Handler placeholder para generar resumen desde estado vacío
  const handleGenerateSummary = () => {
    // TODO: Conectar con lógica real de generación de resumen
    console.log("Generar resumen desde estado vacío - placeholder");
  };

  const handleViewDetail = (item) => {
    history.push(`/summary-detail/${encodeURIComponent(item.id)}`);
  };

  // feature 025 (US4, T042): reintento desde la fila del historial, con los mismos filtros
  // que ya trae el registro (item.filters)
  const handleRetry = async (item) => {
    try {
      await api.postSummary({ ...item.filters, summaryName: item.summaryName });
      fetchHistoryData();
    } catch (err) {
      console.error('Error al reintentar la generación:', err);
    }
  };

  const openAgentsModal = (agents) => {
    if (agents) {
      setSelectedAgents(agents);
      setShowAgentsModal(true);
    }
  }

  // Filtrar items según texto de búsqueda
  const filteredItems = filteringText
    ? historyItems.filter(
      item => {
        const searchText = filteringText.toLowerCase();
        const summaryName = (item.summaryName || "").toLowerCase();
        const supervisors = formatSupervisors(item.filters?.supervisor).toLowerCase();
        const agents = formatAgents(item.filters?.agent).toLowerCase();
        const dateRange = formatDateRange(item.filters).toLowerCase();
        const datetime = (item.datetime || "").toLowerCase();

        return summaryName.includes(searchText) ||
          supervisors.includes(searchText) ||
          agents.includes(searchText) ||
          dateRange.includes(searchText) ||
          datetime.includes(searchText);
      }
    )
    : historyItems;

  // Ordenar items según columna seleccionada
  const sortedItems = [...filteredItems].sort((a, b) => {
    const column = sortingColumn.sortingColumn || "datetime";
    let valueA, valueB;

    // Manejar columnas anidadas
    if (column === "filters.supervisor") {
      valueA = formatSupervisors(a.filters?.supervisor);
      valueB = formatSupervisors(b.filters?.supervisor);
    } else if (column === "filters.agent") {
      valueA = formatAgents(a.filters?.agent);
      valueB = formatAgents(b.filters?.agent);
    } else if (column === "datetime") {
      valueA = a.datetime || "";
      valueB = b.datetime || "";
      // Para ordenamiento de fechas
      const dateA = valueA ? new Date(valueA.replace(' ', 'T')).getTime() : 0;
      const dateB = valueB ? new Date(valueB.replace(' ', 'T')).getTime() : 0;
      if (dateA < dateB) return sortingDescending ? 1 : -1;
      if (dateA > dateB) return sortingDescending ? -1 : 1;
      return 0;
    } else {
      valueA = a[column] || "";
      valueB = b[column] || "";
    }

    // Para ordenamiento de texto
    if (typeof valueA === "string" && typeof valueB === "string") {
      const result = valueA.localeCompare(valueB);
      return sortingDescending ? -result : result;
    }

    // Para ordenamiento numérico
    if (valueA < valueB) return sortingDescending ? 1 : -1;
    if (valueA > valueB) return sortingDescending ? -1 : 1;
    return 0;
  });

  const pagesCount = Math.ceil(sortedItems.length / preferences.pageSize);
  const startIndex = (currentPageIndex - 1) * preferences.pageSize;
  const paginatedItems = sortedItems.slice(startIndex, startIndex + preferences.pageSize);

  // Formatear fecha/hora para mostrar
  const formatDateTime = (datetime) => {
    if (!datetime) return "-";
    try {
      // El backend devuelve formato "YYYY-MM-DD HH:MM:SS", parsearlo correctamente
      const date = new Date(datetime.replace(' ', 'T'));
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return datetime;
    }
  };

  // Definición de columnas para la tabla
  const columnDefinitions = [
    {
      id: "datetime",
      header: t("insights.datetime", { defaultValue: "Fecha y hora" }),
      cell: item => formatDateTime(item.datetime),
      sortingField: "datetime",
      width: 160
    },
    {
      id: "summaryName",
      header: t("insights.summaryName", { defaultValue: "Nombre del resumen" }),
      cell: item => item.summaryName || "-",
      sortingField: "summaryName",
      minWidth: 165
    },
    {
      id: "origin",
      header: t("insights.origin", { defaultValue: "Origen" }),
      cell: item => {
        const origin = item.origin || "MANUAL";
        return t(ORIGIN_LABEL_KEY[origin] || "insights.originManual", { defaultValue: origin });
      },
      sortingField: "origin",
      width: 130
    },
    {
      id: "supervisors",
      header: t("insights.supervisors", { defaultValue: "Supervisores" }),
      cell: item => formatSupervisors(item.filters?.supervisor),
      sortingField: "filters.supervisor",
      width: 160
    },
    {
      id: "agents",
      header: t("insights.agents", { defaultValue: "Agentes" }),
      cell: item => (
        <Link onFollow={() => openAgentsModal(item.filters?.agent)} iconName="group">
          {formatLinkAgents(item.filters?.agent)}
        </Link>
      ),
      sortingField: "filters.agent",
      width: 120
    },
    {
      id: "dateRange",
      header: t("insights.dateRange", { defaultValue: "Rango de fechas" }),
      cell: item => formatDateRange(item.filters),
      minWidth: 130
    },
    {
      id: "coverage",
      header: t("insights.coverage", { defaultValue: "Cobertura" }),
      cell: item => {
        const status = item.status || "COMPLETED";
        return (
          <SpaceBetween size="xxs">
            <StatusIndicator type={STATUS_INDICATOR_TYPE[status] || "success"}>
              {t(STATUS_LABEL_KEY[status] || "insights.statusCompleted", { defaultValue: status })}
            </StatusIndicator>
            <Box variant="small" color="text-body-secondary">
              {item.coverage
                ? `${item.coverage.analyzed}/${item.coverage.found}`
                : (item.itemsAnalyzed ?? "-")}
            </Box>
          </SpaceBetween>
        );
      },
      minWidth: 130
    },
    {
      id: "actions",
      header: t("insights.actions", { defaultValue: "Acciones" }),
      cell: item => (
        // Apilados: la columna no necesita el ancho de los dos links juntos
        <SpaceBetween size="xxs" direction="vertical">
          <Link onFollow={() => handleViewDetail(item)}>
            {t("insights.viewDetail", { defaultValue: "Ver detalle" })}
          </Link>
          {/* T091 (FR-029): sin reintento manual de una quincenal; el cierre siguiente la reemplaza */}
          {canRetryRun(item) && (
            <Link onFollow={() => handleRetry(item)}>
              {t("insights.detail.retry", { defaultValue: "Volver a generar" })}
            </Link>
          )}
        </SpaceBetween>
      ),
      minWidth: 125
    }
  ];

  // Manejador de cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Manejador de cambio de preferencias
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
    if (detail.pageSize !== preferences.pageSize) {
      setCurrentPageIndex(1);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.cxSummaryDescription", {
            defaultValue: "Explorá los resúmenes globales que se generaron en la herramienta. Identificá patrones, emociones y oportunidades de mejora en un solo lugar."
          })}
          actions={
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={handleNewSummary}
            >
              {t("insights.newSummary", { defaultValue: "Nuevo resumen" })}
            </Button>
          }
        >
          {t("insights.callsTitle")}
        </Header>
      }
    >
      <Container
        header={
          <Header variant="h2">
            {t("insights.generatedSummaries", { defaultValue: "Resúmenes generados" })}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder={t("insights.searchSummaries", { defaultValue: "Buscar resúmenes" })}
            filteringAriaLabel={t("insights.searchSummaries", { defaultValue: "Buscar resúmenes" })}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1); // Resetear a primera página al buscar
            }}
          />
          <Table
            columnDefinitions={columnDefinitions}
            items={paginatedItems}
            loading={loading}
            loadingText={t("common.loading")}
            wrapLines={preferences.wrapLines}
            resizableColumns
            // Con la columna «Origen» la tabla supera el ancho del contenedor: «Acciones» queda
            // siempre visible a la derecha (mismo recurso que Users.js)
            stickyColumns={{ first: 0, last: 1 }}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={pagesCount}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                ariaLabels={{
                  nextPageLabel: t("common.nextPage"),
                  previousPageLabel: t("common.previousPage"),
                  pageLabel: pageNumber => `${t("common.page")} ${pageNumber}`
                }}
              />
            }
            preferences={
              <CollectionPreferences
                title={t("contactTable.preferences", { defaultValue: "Preferencias" })}
                confirmLabel={t("contactTable.confirm", { defaultValue: "Confirmar" })}
                cancelLabel={t("contactTable.cancel", { defaultValue: "Cancelar" })}
                preferences={preferences}
                onConfirm={handlePreferencesChange}
                pageSizePreference={{
                  title: t("contactTable.pageSize", { defaultValue: "Tamaño de página" }),
                  options: PAGE_SIZE_OPTIONS.map(value => ({
                    value,
                    label: t("contactTable.pageSizeOption", { count: value, defaultValue: `${value} registros` })
                  }))
                }}
                wrapLinesPreference={{
                  label: t("contactTable.wrapLines", { defaultValue: "Ajustar líneas" }),
                  description: t("contactTable.wrapLinesDescription", { defaultValue: "Marca esta casilla para visualizar los títulos completos y ajustar las líneas automáticamente." })
                }}
                visibleContentPreference={{
                  title: t("contactTable.selectVisibleColumns", { defaultValue: "Seleccionar columnas visibles" }),
                  options: [
                    {
                      label: t("insights.generatedSummaries", { defaultValue: "Resúmenes generados" }),
                      options: [
                        { id: "datetime", label: t("insights.datetime", { defaultValue: "Fecha y hora" }) },
                        { id: "summaryName", label: t("insights.summaryName", { defaultValue: "Nombre del resumen" }) },
                        { id: "origin", label: t("insights.origin", { defaultValue: "Origen" }) },
                        { id: "supervisors", label: t("insights.supervisors", { defaultValue: "Supervisores" }) },
                        { id: "agents", label: t("insights.agents", { defaultValue: "Agentes" }) },
                        { id: "dateRange", label: t("insights.dateRange", { defaultValue: "Rango de fechas" }) },
                        { id: "coverage", label: t("insights.coverage", { defaultValue: "Cobertura" }) },
                        { id: "actions", label: t("insights.actions", { defaultValue: "Acciones" }) }
                      ]
                    }
                  ]
                }}
              />
            }
            empty={
              <Box textAlign="center" color="inherit" padding={{ vertical: "xl" }}>
                <SpaceBetween size="m">
                  <Box variant="p" fontWeight="bold" fontSize="heading-m">
                    {t("insights.noResources", { defaultValue: "Sin recursos disponibles" })}
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    {t("insights.noResourcesDescription", {
                      defaultValue: "No hay recursos para mostrar. Genere un nuevo resumen para visualizarlo en ésta tabla."
                    })}
                  </Box>
                  <Button
                    variant="primary"
                    onClick={handleGenerateSummary}
                  >
                    {t("insights.generateSummary")}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          />
        </SpaceBetween>
      </Container>

      <Modal
        visible={showAgentsModal}
        onDismiss={() => setShowAgentsModal(false)}
        header={t("insights.agentsModal.title", { defaultValue: "Agentes" })}
        footer={
          <Box float="right">
            <Button
              onClick={() => setShowAgentsModal(false)}
              variant="primary"
            >
              {t("common.close")}
            </Button>
          </Box>
        }
      >
        <Table
          columnDefinitions={[
            {
              id: "name",
              header: t("insights.agentsModal.nameColumn", { defaultValue: "Nombre" }),
              cell: item => item,
            }
          ]}
          items={selectedAgents}
          wrapLines={true}
        />
      </Modal>
    </ContentLayout>
  );
}

export default CXSummary;

